import { LngLatBoundsLike, LngLatLike } from 'maplibre-gl'
import { useContext, useEffect } from 'react'
import {
  DEFAULT_BOUNDS_OFFSET,
  DEFAULT_BOUNDS_PADDING,
  DEFAULT_LISTING_ZOOM,
  FIT_TO_BOUNDS_DEFAULT_DURATION,
} from '../constants'
import { fitMapBounds } from '../map-utils'
import { MapContext } from './map-context'

export interface BaseModifiersProps {
  zoom?: number | null
  maxZoom?: number | null
  center?: LngLatLike | null
  bounds?: LngLatBoundsLike | null
  fitToBoundsDuration?: number
  fitToBoundsPadding?: number
  fitToBoundsOffset?: [number, number]
  fitToBoundsMaxZoom?: number
}

/**
 * Component that is in charge of specifically setting map properties (e.g. - bounds, zoom, etc.).
 * These are properties that we do not initialize the map with but set after as the user interacts
 * with the map. For example, the initial map bounds and the bounds passed into here can and will
 * be different.
 * @param The map properties to modify.
 * @returns null No component to draw.
 */
export function BaseModifiers({
  zoom,
  maxZoom,
  center,
  bounds,
  fitToBoundsDuration = FIT_TO_BOUNDS_DEFAULT_DURATION,
  fitToBoundsPadding = DEFAULT_BOUNDS_PADDING,
  fitToBoundsOffset = DEFAULT_BOUNDS_OFFSET,
  fitToBoundsMaxZoom = DEFAULT_LISTING_ZOOM,
}: BaseModifiersProps): null {
  const { map } = useContext(MapContext)

  // ----------------------------------------------------------------------------
  // bounds
  // ----------------------------------------------------------------------------
  useEffect(() => {
    fitMapBounds(map, bounds, {
      duration: fitToBoundsDuration,
      padding: fitToBoundsPadding,
      offset: fitToBoundsOffset,
      maxZoom: fitToBoundsMaxZoom,
    })
  }, [bounds, fitToBoundsDuration, fitToBoundsMaxZoom, fitToBoundsOffset, fitToBoundsPadding, map])

  // ----------------------------------------------------------------------------
  // zoom
  // ----------------------------------------------------------------------------
  useEffect(() => {
    if (map && zoom) {
      map.setZoom(zoom)
    }
  }, [map, zoom])

  useEffect(() => {
    if (map && maxZoom) {
      map.setMaxZoom(maxZoom)
    }
  }, [map, maxZoom])

  // ----------------------------------------------------------------------------
  // center
  // ----------------------------------------------------------------------------
  useEffect(() => {
    if (map && center) {
      map.setCenter(center)
    }
  }, [map, center])

  return null
}

export default BaseModifiers
